import React, { createContext, useState, useContext, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import Notification from '../components/Notification';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const addNotification = useCallback((type, message, link = null, options = {}) => {
    const id = options.id || Date.now();

    setNotifications(prev => {
      // Remove existing notification with same id (for updates)
      const filtered = options.id ? prev.filter(n => n.id !== options.id) : prev;

      // Vérifier si une notification identique existe déjà
      const hasIdentical = filtered.some(
        notif => notif.message === message && notif.type === type
      );

      if (hasIdentical) return filtered;

      const maxNotifications = 3;
      const newNotifications = [...filtered, { id, type, message, link }];
      return newNotifications.slice(-maxNotifications);
    });

    if (!options.persistent) {
      const timeout = type === 'error' ? 8000 : 5000;
      setTimeout(() => removeNotification(id), timeout);
    }

    return id;
  }, [removeNotification]);

  const contextValue = {
    addNotification,
    removeNotification,
    addSuccessNotification: useCallback((message) =>
      addNotification('success', message), [addNotification]),
    addWarningNotification: useCallback((message) =>
      addNotification('warning', message), [addNotification]),
    addErrorNotification: useCallback((message) =>
      addNotification('error', message), [addNotification]),
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <AnimatePresence>
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            {...notification}
            onClose={removeNotification}
          />
        ))}
      </AnimatePresence>
    </NotificationContext.Provider>
  );
};